import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { ProposalHeader } from "@/components/proposal/ProposalHeader";
import { ProposalHero } from "@/components/proposal/ProposalHero";
import { ProposalFacts } from "@/components/proposal/ProposalFacts";
import { ProposalSolution } from "@/components/proposal/ProposalSolution";
import { ProposalTimeline } from "@/components/proposal/ProposalTimeline";
import { ProposalPricing } from "@/components/proposal/ProposalPricing";
import { ProposalTerms } from "@/components/proposal/ProposalTerms";
import { ProposalRespond, AcceptDialog, ChangesDialog, MobileActionBar } from "@/components/proposal/ProposalRespond";
import { ProposalFooter } from "@/components/proposal/ProposalFooter";
import { ProposalSkeleton, ProposalUnavailable, StateBanner } from "@/components/proposal/ProposalStates";
import { scrollToSection } from "@/components/proposal/proposalUtils";

/**
 * Public web proposal: /tilbud/vis/:token. No auth – the token maps to one immutable quote version.
 * `?preview=1` (used from the internal quote page) skips view tracking.
 */
export default function PublicQuotePage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const preview = params.get("preview") === "1";
  const qc = useQueryClient();
  const key = ["public-quote", token];
  const { data, isLoading, error } = useQuery({ queryKey: key, queryFn: () => get(`/public/quotes/${token}`, preview ? { preview: 1 } : undefined), retry: false, refetchOnWindowFocus: false });
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [changesOpen, setChangesOpen] = useState(false);

  useEffect(() => {
    if (data?.quote) document.title = `Tilbud ${data.quote.quote_number} – ${data.company?.name || "Stord Innkvartering"}`;
    return () => { document.title = "Stord Innkvartering – Driftsplattform"; };
  }, [data]);

  if (isLoading) return <ProposalSkeleton />;
  if (error || !data) return <ProposalUnavailable status={error?.response?.status} message={error?.response?.data?.detail} />;

  const refresh = () => { qc.invalidateQueries({ queryKey: key }); setTimeout(() => scrollToSection("svar"), 250); };

  return (
    <div className="min-h-screen proposal-paper text-foreground antialiased" data-testid="proposal-page">
      <ProposalHeader data={data} onAccept={() => setAcceptOpen(true)} />
      <ProposalHero data={data} />
      <ProposalFacts data={data} />
      <StateBanner data={data} />
      <main className="mt-12 pb-20 sm:pb-0">
        <ProposalSolution data={data} />
        <ProposalTimeline data={data} />
        <ProposalPricing data={data} />
        <ProposalTerms data={data} />
        <ProposalRespond data={data} onAccept={() => setAcceptOpen(true)} onChanges={() => setChangesOpen(true)} />
      </main>
      <ProposalFooter data={data} />
      <MobileActionBar data={data} onAccept={() => setAcceptOpen(true)} onChanges={() => setChangesOpen(true)} />
      <AcceptDialog open={acceptOpen} onOpenChange={setAcceptOpen} data={data} onDone={refresh} />
      <ChangesDialog open={changesOpen} onOpenChange={setChangesOpen} data={data} onDone={refresh} />
    </div>
  );
}
